import React from 'react';
import { classes } from 'src/modules/shared/utils/styles.util';

type AiBadgeSize = 'sm' | 'md';
type AiBadgeAppearance = 'filled' | 'soft' | 'outline';
export type AiBadgeVariant = 'brand' | 'neutral';

interface AiBadgeProps {
  size?: AiBadgeSize;
  className?: string;
  variant?: AiBadgeVariant;
  appearance?: AiBadgeAppearance;
}

const iconSizeMap: Record<AiBadgeSize, string> = {
  sm: 'h-5 w-5',
  md: 'h-6 w-6',
};

const filledIconColorMap: Record<AiBadgeVariant, string> = {
  brand: 'text-blue-500',
  neutral: 'text-secondary-400',
};

const tonedIconColorMap: Record<AiBadgeVariant, string> = {
  brand: 'text-blue-600',
  neutral: 'text-secondary-500',
};

/** Filled AI robot glyph reserved for the AI Agent avatar. */
export const AiRobotFilledIcon: React.FC<{ className?: string }> = ({
  className = '',
}) => (
  <svg
    viewBox="1.5 1.25 21 21"
    aria-hidden="true"
    className={className}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="4.2" r="1.15" fill="currentColor" />
    <path
      d="M12 5.45V6.95"
      stroke="currentColor"
      strokeWidth="0.95"
      strokeLinecap="round"
    />
    <rect
      x="5.4"
      y="6.95"
      width="13.2"
      height="11.95"
      rx="5.6"
      fill="currentColor"
    />
    <path
      d="M4.5 11.3V13.5M19.5 11.3V13.5"
      stroke="currentColor"
      strokeWidth="0.95"
      strokeLinecap="round"
    />
    <circle cx="9.45" cy="12.1" r="0.95" fill="white" />
    <circle cx="14.55" cy="12.1" r="0.95" fill="white" />
    <path
      d="M9 15.05C9.95 15.95 10.95 16.35 12 16.35C13.05 16.35 14.05 15.95 15 15.05"
      stroke="white"
      strokeWidth="0.95"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Outlined AI robot glyph for navigation and page-heading contexts. */
export const AiRobotOutlineIcon: React.FC<{ className?: string }> = ({
  className = '',
}) => (
  <svg
    viewBox="1.5 1.25 21 21"
    aria-hidden="true"
    className={className}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="4.2" r="1.15" fill="currentColor" />
    <path
      d="M12 5.45V6.95"
      stroke="currentColor"
      strokeWidth="0.95"
      strokeLinecap="round"
    />
    <rect
      x="5.4"
      y="6.95"
      width="13.2"
      height="11.95"
      rx="5.6"
      stroke="currentColor"
      strokeWidth="0.95"
    />
    <path
      d="M4.5 11.3V13.5M19.5 11.3V13.5"
      stroke="currentColor"
      strokeWidth="0.95"
      strokeLinecap="round"
    />
    <circle cx="9.45" cy="12.1" r="0.95" fill="currentColor" />
    <circle cx="14.55" cy="12.1" r="0.95" fill="currentColor" />
    <path
      d="M9 15.05C9.95 15.95 10.95 16.35 12 16.35C13.05 16.35 14.05 15.95 15 15.05"
      stroke="currentColor"
      strokeWidth="0.95"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const AiBadge: React.FC<AiBadgeProps> = ({
  size = 'sm',
  className = '',
  variant = 'brand',
  appearance = 'filled',
}) => {
  const ariaLabel = 'Estado gestionado por IA';
  const iconColorClass =
    appearance === 'filled'
      ? filledIconColorMap[variant]
      : tonedIconColorMap[variant];

  return (
    <span
      role="status"
      aria-label={ariaLabel}
      title={ariaLabel}
      className={classes(
        'inline-flex shrink-0 select-none items-center justify-center pointer-events-none',
        iconSizeMap[size],
        className
      )}
    >
      <AiRobotOutlineIcon
        className={classes('h-full w-full', iconColorClass)}
      />
    </span>
  );
};
